import type { ProjectStatus } from './types'

/**
 * The statuses the server knows (its `ProjectStatuses`): a project is a draft, waiting for review, approved, or cancelled. The app
 * tells a little more apart for its own screens (a draft being worked on, one sent back for changes, an approved one that is complete),
 * and every one of those is one of these four to the server.
 */
export const SERVER_STATUSES = ['DRAFT', 'PENDING_REVIEW', 'APPROVED', 'CANCELLED'] as const
export type ServerStatus = (typeof SERVER_STATUSES)[number]

/** The server's word for one of the app's statuses. */
export function toServerStatus(status: ProjectStatus): ServerStatus {
  switch (status) {
    case 'IN_REVIEW':
      return 'PENDING_REVIEW'
    case 'APPROVED':
    case 'COMPLETED':
      return 'APPROVED'
    case 'CANCELLED':
      return 'CANCELLED'
    default:
      return 'DRAFT'
  }
}

/** What the server said, in any spelling ("PENDING_REVIEW", "Pending review", "pendingReview"), or `undefined` if it is none of its four. */
export function serverStatusOf(word: string | null | undefined): ServerStatus | undefined {
  const normalised = (word ?? '').trim().replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase().replace(/[\s-]+/g, '_')
  return (SERVER_STATUSES as readonly string[]).includes(normalised) ? (normalised as ServerStatus) : undefined
}

/** The app's status for what the server said, for a project this browser knows nothing else about. */
export function fromServerStatus(status: ServerStatus): ProjectStatus {
  switch (status) {
    case 'PENDING_REVIEW':
      return 'IN_REVIEW'
    case 'APPROVED':
      return 'APPROVED'
    case 'CANCELLED':
      return 'CANCELLED'
    case 'DRAFT':
      return 'DRAFT'
  }
}

/**
 * The status to show: the server's, unless it already agrees with the finer one kept here. A project that is "in progress" or
 * "completed" here is a draft or approved to the server, so the server saying "draft" or "approved" is no news and does not
 * wipe out the detail. When the server says something different (it went to review elsewhere, someone approved it), the server wins.
 */
export function reconcileStatus(server: string | null | undefined, local: ProjectStatus | undefined): ProjectStatus {
  const known = serverStatusOf(server)
  if (!known) return local ?? 'DRAFT'
  return local && toServerStatus(local) === known ? local : fromServerStatus(known)
}
